/**
 * @module app.pp
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { CustomViewService, CustomViewReference } from 'app/shared/services/custom-view-service';
import { IDocumentsConfig } from 'app/core/models/essette-interfaces';
import { DocumentsService } from 'app/core/services/documents-service';
import { apiCall } from 'app/shared/lib/api/api';
import { printElement, lookupPrintOptions, IPrintOptions, prepareElementForPrint } from 'app/shared/lib/printing';
import { I18N } from 'aurelia-i18n';
import { IWizardStep } from 'app/core/components/wizard/interfaces';
import { focusFirst } from 'app/shared/lib/focus';
import * as Core from 'app/core/models/essette-api';
import * as UM from 'app/um/models/essette-api';
import { guid } from 'app/shared/lib/guid';

type RadioSelection = 'none' | 'paper' | 'electronic' | 'both';

@autoinject()
export class AttachDocumentationStep extends ComponentBase implements IWizardStep {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected auth: UM.Auth = {};

    @bindable.string()
    protected scope: string = '';

    @bindable.boolean()
    protected readonly: boolean = false;

    @bindable.string()
    protected completedKey: string = 'pp:auths.documentation.completed';

    protected selection: RadioSelection = 'none';

    public views: CustomViewReference[] = [];
    public canSkip: boolean = false;
    public canContinue: boolean = true;
    public canPerformFinalSubmit: boolean = false;

    protected documentsConfig: IDocumentsConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        categoryFlagCodes: ['PP.AuthCreate'],
        addTargetProperty: (document: UM.AuthDocument) => { document.auth = { id: this.auth.id } }
    };

    protected documentsGridConfig: IDocumentsConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        categoryFlagCodes: ['PP.View'],
        addTargetProperty: (document: UM.AuthDocument) => { document.auth = { id: this.auth.id } }
    };

    protected documentCategory: Core.DocumentCategory | null = null;


    ////****************** Element References ***************************************////

    protected id: guid = guid();
    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private custom: CustomViewService,
        private readonly i18n: I18N,
        private readonly documentService: DocumentsService,
        private $element: Element,
        ...rest: any[]) {
        super(...rest);
        this.logger.source = this;
        this.documentService.configure(this.documentsConfig);
        this.setCategory();
    }

    protected attached(): void {
        this.loadCustomViews();
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    public skipStep(): void { }
    public continueStep(): void { }
    public openStep(): void {
        if(!this.readonly)
            setTimeout(() => focusFirst(this.$element), 250);
    }
    public async closeStep(): Promise<void> { }
    public async finalSubmit(): Promise<void> { }

    private async loadCustomViews(): Promise<void> {
        const baseUrl = 'app/core/templates/cover-sheets/';
        const key = `core.cover-sheets.${this.scope}`;
        this.views = await this.custom.resolveCustomViewUrls(baseUrl, key, this.env.tenant.code);
    }

    // Set the document config locked category
    private async setCategory() {
        await this.loadDocumentCategory();

        if(this.documentCategory?.id) {
            this.documentsGridConfig.categoryId = this.documentCategory.id;
        }
    }

    protected async print(target: Element, titleKey: string): Promise<void> {
        const title = this.i18n.tr(titleKey);
        const printOptions = lookupPrintOptions(this.env.config, true, title);
        await printElement(target, printOptions);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadDocumentCategory(): Promise<void> {
        const includes: string[] = ['flags'];

        try {
            this.state.beginLoad();
            const response = await this.documentService.loadCategories({}, includes);

            if(response.results.length == 1) {
                this.documentCategory = response.results[0];
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
